/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  StationRevenueSharingInterFace,
  StationRevenueSharingRequestInterface,
  StationRevenueSharingResponseInterface,
} from '../../interface/revenue-management.interface';
// import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
// import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
// import FormatUtcString from '../../shared-functions/FormatUtcString';
import revenueApi from '../../utils/api/revenue';

import { setNotificationMessage } from './NotificationManagementSlice';

interface RevenueSharingInitialProperties {
  revenueList: StationRevenueSharingInterFace[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: 'asc' | 'desc' | null;
  sortField: string | null;
}

// a thunk to fetch accounts
export const fetchStationRevenueSharingList = createAsyncThunk<
  StationRevenueSharingResponseInterface,
  StationRevenueSharingRequestInterface
>(
  'revenue-station-sharing-account/fetchStationRevenueSharingList',
  async (data: StationRevenueSharingRequestInterface, { dispatch }) => {
    try {
      const response = await revenueApi.fetchRevenueSharing(
        removeEmptyKeys({ ...data, groupBy: 'station' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// a thunk to refresh station revenue sharing list
export const refreshStationRevenueSharingList = createAsyncThunk<
  StationRevenueSharingResponseInterface,
  StationRevenueSharingRequestInterface
>(
  'revenue-station-sharing-account/refreshStationRevenueSharingList',
  async (data: StationRevenueSharingRequestInterface) => {
    const response = await revenueApi.fetchRevenueSharing(
      removeEmptyKeys({ ...data, groupBy: 'station' }),
    );
    return response.data;
  },
);

// a thunk to fetch devices filters
export const fetchStationRevenueSharingFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('payment-management/fetchStationRevenueSharingFilters', async (_, { dispatch }) => {
  try {
    const response = await revenueApi.fetchRevenueSharingListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// Apply default filters
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

/**
 * Filter date format func
 * @param {any} date - The date string.
 * @returns {any} - The returned value.
 */
const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'Date Range',
    key: 'startDate_endDate',
    value: '',
    key1: 'startDate',
    value1: formatDate(yesterday),
    key2: 'endDate',
    value2: formatDate(today),
    type: 'dateRange',
    listOfValue: [],
    applyFilter: true,
    enable: true,
    disableClearFilter: true,
    disableClearFilterMessage: 'Date filter is required!',
    FEAddedFilter: true, // This is used to identify if the filter is added from FE
  },
];

// Initial state
const initialState: RevenueSharingInitialProperties = {
  revenueList: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const stationRevenueSharingSlice = createSlice({
  name: 'revenue-station-sharing-account',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateStationRevenueSharingFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disableStationRevenueSharingApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchStationRevenueSharingList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStationRevenueSharingList.fulfilled, (state, action) => {
        state.loading = false;
        state.revenueList = action.payload?.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchStationRevenueSharingList.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(refreshStationRevenueSharingList.fulfilled, (state, action) => {
      state.revenueList = action.payload?.results;
      state.limit = action.payload.limit;
      state.page = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    builder
      .addCase(fetchStationRevenueSharingFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchStationRevenueSharingFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key),
          );
        },
      )
      .addCase(fetchStationRevenueSharingFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  updateStationRevenueSharingFilters,
  disableStationRevenueSharingApplied,
  setSortData,
} = stationRevenueSharingSlice.actions;

// Export the reducer
export default stationRevenueSharingSlice.reducer;
